import { makeStyles, Button, Theme, CircularProgress } from "@material-ui/core";
import React, { MouseEvent, ReactElement } from "react";
import { usePartster } from "../contexts/PartsterContext";
import { useVi } from "../contexts/ViContext";
import { Translator } from "../services";
import { BundlePartsFooter, DamagesFooter, OperationsFooter } from "./";

const useStyles = makeStyles((theme: Theme) => ({
  footer: () => ({
    position: "relative",
    boxSizing: "border-box",
    gridArea: "footer",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    display: "flex",
    flexFlow: "row-reverse nowrap",
    placeContent: "flex-end",
    zIndex: 0,
  }),
  footerContent: {
    [theme.breakpoints.down("sm")]: {},
    gridRow: "footer",
    gridColumn: "2/3",
    position: "relative",
    boxSizing: "border-box",
    overflow: "hidden",
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px 0`,
    minHeight: 0,
    maxHeight: "30vh",
    height: "min-content",
    maxWidth: theme.breakpoints.values.lg,
  },
  footerButtons: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gridRow: "footer",
    gridColumn: "1 / 2",
    margin: `${theme.spacing(2)}px 0 ${theme.spacing(4)}px`,
  },
  button: {
    minWidth: "10rem",
    maxWidth: "15rem",
    clipPath:
      "polygon(0 0, calc(100% - 0.382rem) 0, 100% 50%,calc(100% - 0.382rem) 100%, 0 100%)",
    justifyContent: "flex-end",
    borderRadius: 0,
    transition: "all 0.382s ease-out",
    marginLeft: "-2rem",
    paddingLeft: "2rem",
    "&:not([disabled])": {
      background: `linear-gradient(to right, transparent 0%, ${theme.palette.secondary.light} 30%, ${theme.palette.secondary.main} 100%)`,
    },
    "&:hover": {
      background: `linear-gradient(to right, transparent 0%, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.dark} 100%)`,
    },
    "&.active": {
      background: `linear-gradient(to right, transparent 0%, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.dark} 100%)`,
      marginLeft: "0",
    },
    "& + button": {
      marginTop: theme.spacing(1),
    },
  },
  shadow: {
    height: "30vh",
    width: "100%",
    boxShadow: theme.shadows[4],
    gridRow: "footer",
    gridColumn: "1/3",
    position: "relative",
    zIndex: 1,
    pointerEvents: "none",
  },
  loader: { marginLeft: "0.382rem" },
}));

function Footer(): ReactElement {
  const classes = useStyles();
  const { isLoading: partsterIsLoading } = usePartster();
  const {
    claimDamagedParts,
    aggregatedPartDamages,
    hoverFilter,
    setHoverFilter,
    footerTab,
    bundleParts,
    setFooterTab,
    isFilterMatch,
  } = useVi();

  const handleClick = (e: MouseEvent) => {
    const id =
      (e.target instanceof HTMLElement && (e.target.id || e.target.parentElement?.id)) ||
      null;
    setFooterTab(footerTab === id ? null : id);
  };

  const buttons = [
    ["damages", aggregatedPartDamages],
    ["operations", claimDamagedParts],
  ] as [string, any[]][];

  return (
    <>
      {Boolean(footerTab) ? <div key="shadow" className={classes.shadow} /> : null}
      {footerTab === "damages" ? (
        <DamagesFooter
          key="damages"
          className={classes.footerContent}
          {...{
            aggregatedPartDamages,
            hoverFilter,
            isFilterMatch,
            setHoverFilter,
          }}
        />
      ) : null}
      {footerTab === "operations" ? (
        <OperationsFooter
          key="operations"
          className={classes.footerContent}
          {...{
            claimDamagedParts,
            hoverFilter,
            isFilterMatch,
            setHoverFilter,
          }}
        />
      ) : null}
      {footerTab === "bundleParts" ? (
        <BundlePartsFooter
          key="bundleParts"
          className={classes.footerContent}
          {...{
            bundleParts,
          }}
        />
      ) : null}
      <div className={classes.footerButtons}>
        {buttons.map(([id, data]: [string, any[]]) => (
          <Button
            key={id}
            id={id}
            color="secondary"
            variant="contained"
            className={[classes.button, ...(footerTab === id ? ["active"] : [])].join(
              " "
            )}
            onClick={handleClick}
            disabled={data === undefined || data.length < 1}
          >
            {Translator.translate(`components.footer.${id}`)}
          </Button>
        ))}
        <Button
          key="bundleParts"
          id="bundleParts"
          color="secondary"
          variant="contained"
          className={[
            classes.button,
            ...(footerTab === "bundleParts" ? ["active"] : []),
          ].join(" ")}
          onClick={handleClick}
          disabled={bundleParts === undefined || bundleParts.length < 1}
        >
          {Translator.translate(`components.footer.bundleParts`)}
          {partsterIsLoading ? (
            <CircularProgress color="primary" size="1rem" className={classes.loader} />
          ) : (
            ""
          )}
        </Button>
      </div>
    </>
  );
}
export default React.memo(Footer);
